import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import { saveCartItems } from "../../utils/cartUtils";
import { toLocalizedNumber, toLocalizedPrice } from "../../utils/localeUtils";
import { getImageUrl } from "../../utils/imageUtils";
import { usePopup } from "../../context/PopupContext";
import { FaShoppingCart, FaMapMarkerAlt } from "react-icons/fa";
import "./Cart.css";

const Cart = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showConfirm } = usePopup();
  const [, setUser] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load user
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (userObj.role !== "consumer") {
          navigate("/");
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    } else {
      navigate("/login");
    }

    // Load cart items
    loadCartItems();

    // Listen for storage changes (when cart is updated from other tabs/pages)
    const handleStorageChange = (e) => {
      if (e.key === "cart") {
        loadCartItems();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    // Also reload cart when page becomes visible (in case cart was updated in same tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadCartItems();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [navigate]);

  const loadCartItems = () => {
    setLoading(true);
    // Load cart from localStorage
    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      try {
        const cartData = JSON.parse(savedCart);
        setCartItems(cartData);
      } catch (error) {
        console.error("Error loading cart:", error);
        setCartItems([]);
      }
    } else {
      setCartItems([]);
    }
    setLoading(false);
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(itemId);
      return;
    }

    const updatedItems = cartItems.map((item) =>
      item._id === itemId
        ? {
            ...item,
            quantity: newQuantity,
            subtotal: item.product.price * newQuantity,
          }
        : item,
    );
    setCartItems(updatedItems);
    saveCartItems(updatedItems);
  };

  const removeItem = (itemId) => {
    const updatedItems = cartItems.filter((item) => item._id !== itemId);
    setCartItems(updatedItems);
    saveCartItems(updatedItems);
  };

  const clearCart = () => {
    showConfirm({
      title: t("cart.clearCartTitle"),
      message: t("cart.clearCartMessage"),
      confirmLabel: t("cart.clearCart"),
      onConfirm: () => {
        setCartItems([]);
        saveCartItems([]);
      },
    });
  };

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = cartItems.reduce((sum, item) => sum + item.subtotal, 0);
  const deliveryFee = subtotal > 500 ? 0 : 50;
  const total = subtotal + deliveryFee;

  if (loading) {
    return (
      <div className="cart-page">
        <Header />
        <div className="cart-loading-state">
          <div className="cart-spinner"></div>
          <p>{t("cart.loadingCart")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <Header />

      <div className="cart-container">
        <div className="cart-header">
          <h1 className="cart-title">{t("cart.title")}</h1>
          {cartItems.length > 0 && (
            <button onClick={clearCart} className="cart-clear-cart-btn">
              {t("cart.clearCart")}
            </button>
          )}
        </div>

        {cartItems.length === 0 ? (
          <div className="cart-empty-cart">
            <div className="cart-empty-cart-icon" aria-hidden>
              <FaShoppingCart />
            </div>
            <h2>{t("cart.emptyTitle")}</h2>
            <p>{t("cart.emptyDesc")}</p>
            <Link to="/market" className="cart-btn-shop-now">
              {t("landing.startShopping")}
            </Link>
          </div>
        ) : (
          <div className="cart-content">
            {/* Cart Items */}
            <div className="cart-items-section">
              <div className="cart-items">
                {cartItems.map((item) => (
                  <div key={item._id} className="cart-item">
                    <Link
                      to={`/products/${item.product._id}`}
                      className="cart-item-image"
                    >
                      <img
                        src={getImageUrl(item.product.image)}
                        alt={item.product.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/images/deafultpic.png";
                        }}
                      />
                      {item.product.isOrganic && (
                        <span className="cart-organic-tag">
                          {t("common.organic")}
                        </span>
                      )}
                    </Link>

                    <div className="cart-item-details">
                      <Link
                        to={`/products/${item.product._id}`}
                        className="cart-item-name"
                      >
                        {item.product.name}
                      </Link>
                      <div className="cart-item-farmer">
                        <span className="cart-item-farmer-line">
                          By {item.product.farmer.name}
                          {item.product.farmer.district && (
                            <>
                              {" "}
                              <span
                                className="cart-item-farmer-loc"
                                aria-hidden
                              >
                                <FaMapMarkerAlt />{" "}
                                {item.product.farmer.district}
                              </span>
                            </>
                          )}
                        </span>
                      </div>
                      <div className="cart-item-price">
                        Rs. {toLocalizedPrice(item.product.price)} /{" "}
                        {item.product.unit}
                      </div>

                      <div className="cart-item-actions">
                        <div className="cart-item-quantity">
                          <label>{t("cart.qty")}</label>
                          <div className="cart-quantity-controls">
                            <button
                              onClick={() =>
                                updateQuantity(item._id, item.quantity - 1)
                              }
                              className="cart-qty-btn"
                              disabled={item.quantity <= 1}
                            >
                              −
                            </button>
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => {
                                const val = parseInt(e.target.value) || 1;
                                updateQuantity(item._id, Math.max(1, val));
                              }}
                              min="1"
                              className="cart-qty-input"
                            />
                            <button
                              onClick={() =>
                                updateQuantity(item._id, item.quantity + 1)
                              }
                              className="cart-qty-btn"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="cart-item-subtotal">
                      <div className="cart-subtotal-label">
                        {t("cart.subtotal")}
                      </div>
                      <div className="cart-subtotal-value">
                        Rs. {toLocalizedPrice(item.subtotal)}
                      </div>
                    </div>

                    <button
                      onClick={() => removeItem(item._id)}
                      className="cart-remove-item-btn"
                      title={t("cart.removeItem")}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Cart Summary */}
            <div className="cart-summary-section">
              <div className="cart-summary">
                <h2 className="cart-summary-title">{t("cart.orderSummary")}</h2>

                <div className="cart-summary-row">
                  <span>
                    {t("cart.items", { count: toLocalizedNumber(totalItems) })}
                  </span>
                  <span>Rs. {toLocalizedPrice(subtotal)}</span>
                </div>

                <div className="cart-summary-row">
                  <span>{t("cart.deliveryFee")}</span>
                  <span>
                    {deliveryFee === 0 ? (
                      <span className="cart-free-delivery">
                        {t("common.free")}
                      </span>
                    ) : (
                      `Rs. ${toLocalizedPrice(deliveryFee)}`
                    )}
                  </span>
                </div>

                {subtotal < 500 && (
                  <div className="cart-delivery-info">
                    <small>
                      {t("cart.freeDeliveryHint", {
                        amount: toLocalizedPrice(500 - subtotal),
                      })}
                    </small>
                  </div>
                )}

                <div className="cart-summary-divider"></div>

                <div className="cart-summary-total">
                  <span>{t("cart.total")}</span>
                  <span>Rs. {toLocalizedPrice(total)}</span>
                </div>

                <Link to="/checkout" className="cart-btn-checkout">
                  {t("cart.proceedToCheckout")}
                </Link>

                <Link to="/market" className="cart-continue-shopping">
                  {t("cart.continueShopping")}
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
